import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";
import PageHero from "@/components/landing/PageHero";
import PageBottomCTA from "@/components/landing/PageBottomCTA";
import { PAGES } from "@/constants/testIds";
import { WEB_REGISTER_URL, formatNaira, formatUsd } from "@/constants/site";
import { usePublicPlans } from "@/hooks/usePublicPlans";
import { captureEvent } from "@/lib/analytics";
import PageMeta from "@/components/PageMeta";

const PLAN_DETAILS = {
  solo: {
    cta: "Start with Solo",
    testId: PAGES.pricingCardSolo,
    ctaTestId: PAGES.pricingCtaSolo,
    features: ["Sales, inventory & customer credit", "Offline-ready mobile workflows", "Web workspace and business insights"],
  },
  starter: {
    cta: "Start with Starter",
    testId: PAGES.pricingCardStarter,
    ctaTestId: PAGES.pricingCtaStarter,
    features: ["Sales, inventory & customer credit", "Offline-ready mobile workflows", "Web workspace and business insights"],
  },
  growth: {
    cta: "Start with Growth",
    testId: PAGES.pricingCardGrowth,
    ctaTestId: PAGES.pricingCtaGrowth,
    features: ["Everything in Starter", "Role-based staff access", "Multi-branch visibility and analytics"],
  },
  scale: {
    cta: "Start with Scale",
    testId: PAGES.pricingCardEnterprise,
    ctaTestId: PAGES.pricingCtaEnterprise,
    features: ["Everything in Growth", "Higher operating limits", "A wider view across locations"],
  },
};

const COMPARE_BASE = [
  { label: "Offline-first sales recording", o: true, s: true, g: true, e: true },
  { label: "Inventory & debt tracking", o: true, s: true, g: true, e: true },
  { label: "Multilingual assistant", o: true, s: true, g: true, e: true },
  { label: "Staff roles & permissions", o: true, s: true, g: true, e: true },
  { label: "Analytics & business insights", o: true, s: true, g: true, e: true },
  { label: "Restock recommendations", o: true, s: true, g: true, e: true },
  { label: "Demand forecasting", o: true, s: true, g: true, e: true },
];

export default function Pricing() {
  const [billingRegion, setBillingRegion] = useState("nigeria");
  const { plans, source } = usePublicPlans();
  const tiers = plans.map((plan) => ({
    ...plan,
    id: plan.code,
    cadence: "per month",
    desc: plan.description,
    href: WEB_REGISTER_URL,
    featured: plan.code === "growth",
    ...PLAN_DETAILS[plan.code],
    features: [
      `${plan.maxBranches === 1 ? "1 branch" : `Up to ${plan.maxBranches.toLocaleString()} branches`}`,
      `Up to ${plan.maxStaff.toLocaleString()} team members`,
      `Up to ${plan.maxProducts.toLocaleString()} products`,
      `${Number(plan.includedCredits || 0).toLocaleString()} AI credits each month`,
      ...(PLAN_DETAILS[plan.code]?.features || []),
    ],
  }));
  const planByCode = Object.fromEntries(tiers.map((plan) => [plan.code, plan]));
  const compare = [
    ...COMPARE_BASE.slice(0, 4),
    { label: "Maximum branches", o: planByCode.solo?.maxBranches, s: planByCode.starter?.maxBranches, g: planByCode.growth?.maxBranches, e: planByCode.scale?.maxBranches },
    { label: "Maximum team members", o: planByCode.solo?.maxStaff, s: planByCode.starter?.maxStaff, g: planByCode.growth?.maxStaff, e: planByCode.scale?.maxStaff },
    { label: "Maximum products", o: planByCode.solo?.maxProducts?.toLocaleString(), s: planByCode.starter?.maxProducts?.toLocaleString(), g: planByCode.growth?.maxProducts?.toLocaleString(), e: planByCode.scale?.maxProducts?.toLocaleString() },
    { label: "Included AI credits / month", o: planByCode.solo?.includedCredits, s: planByCode.starter?.includedCredits, g: planByCode.growth?.includedCredits, e: planByCode.scale?.includedCredits },
    ...COMPARE_BASE.slice(4),
  ];
  const solo = planByCode.solo;
  const pricingFaq = [
    { q: "How much does Shopbook start at?", a: `Solo starts at ${formatNaira(solo?.priceMinor || 0)} per month for Nigeria billing or ${formatUsd(solo?.usdPriceMinor || 0)} per month for international billing. Your account confirms the applicable currency before checkout.` },
    { q: "What changes between plans?", a: "The operating foundation stays the same. Plans mainly change the number of branches, team members, and products the workspace can support." },
    { q: "Do you offer longer billing periods?", a: "Yes. Quarterly and annual options are shown inside the workspace together with any current savings before you pay." },
    { q: "How do payments and trials work?", a: "Eligible businesses can start a seven-day trial without an automatic charge. Paid access begins only after you choose a plan, complete secure checkout, and Shopbook verifies the payment." },
    { q: "How do AI credits work?", a: "Each plan includes monthly AI credits. An assistant message uses 0.5 credit and product-image generation uses 5 credits. You can buy a top-up when needed. WhatsApp and Telegram alerts are still coming soon; their prices will be shown before you can enable them." },
  ];

  const selectBillingRegion = (region) => {
    setBillingRegion(region);
    captureEvent("pricing_region_selected", { region, pricing_source: source });
  };

  return (
    <>
      <PageMeta title="Pricing" description="Choose Shopbook by the branches, team members, and products your retail operation needs, with Nigeria and international billing views." />
      <PageHero
        eyebrow="Pricing"
        title="Simple pricing."
        italicTail="Built for real retail."
        intro="Choose by the capacity your business needs today. Every plan keeps the essential operating record together across sales, stock, customer credit, staff, and insights."
      />

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="max-w-xl text-sm text-[#4B5E5E]">Preview pricing by billing region. Your account confirms the available currency before checkout.</p>
            <div className="inline-flex w-fit rounded-full bg-[#F1F5F3] p-1">
              {[["nigeria", "Nigeria · NGN"], ["international", "International · USD"]].map(([value, label]) => (
                <button key={value} type="button" onClick={() => selectBillingRegion(value)} aria-pressed={billingRegion === value} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${billingRegion === value ? "bg-[#0E6B6B] text-white" : "text-[#4B5E5E]"}`}>{label}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
            {tiers.map((t) => (
              <article
                key={t.id}
                data-testid={t.testId}
                className={`relative rounded-3xl p-7 flex flex-col ${
                  t.featured
                    ? "bg-[#0A3B3B] text-white border border-[#0E6B6B]"
                    : "bg-[#F7FAF9] border border-[#0E6B6B]/12"
                }`}
              >
                {t.featured && (
                  <span className="absolute -top-3 left-8 rounded-full bg-[#FFD8A8] text-[#0A2424] px-3 py-1 text-xs font-bold uppercase tracking-widest">
                    Best for growing teams
                  </span>
                )}
                <div className={`text-xs uppercase tracking-widest font-bold ${t.featured ? "text-[#FFD8A8]" : "text-[#D96C4A]"}`}>
                  {t.name}
                </div>
                <div className={`mt-3 font-display text-5xl md:text-6xl font-semibold ${t.featured ? "" : "text-[#0A2424]"}`}>
                  {billingRegion === "nigeria" ? formatNaira(t.priceMinor) : formatUsd(t.usdPriceMinor)}
                </div>
                <div className={`text-sm mt-1 ${t.featured ? "text-white/70" : "text-[#4B5E5E]"}`}>
                  {t.cadence}
                </div>
                <p className={`mt-5 text-[15px] ${t.featured ? "text-white/80" : "text-[#3a4a4a]"}`}>
                  {t.desc}
                </p>
                <ul className="mt-7 space-y-3 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-[15px]">
                      <span className={`mt-0.5 h-5 w-5 rounded-full flex items-center justify-center flex-none ${
                        t.featured ? "bg-[#FFD8A8] text-[#0A2424]" : "bg-[#0E6B6B] text-white"
                      }`}>
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                      <span className={t.featured ? "text-white/90" : "text-[#1c2c2c]"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={t.href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={t.ctaTestId}
                  onClick={() => captureEvent("pricing_plan_selected", { plan: t.code, region: billingRegion, pricing_source: source })}
                  className={`mt-8 inline-flex items-center justify-center rounded-full px-6 py-3.5 text-base font-semibold transition-colors ${
                    t.featured
                      ? "bg-[#FFD8A8] text-[#0A2424] hover:bg-white"
                      : "bg-[#0E6B6B] text-white hover:bg-[#138282]"
                  }`}
                >
                  {t.cta}
                </Link>
              </article>
            ))}
          </div>
          <p className="mt-6 text-center text-sm text-[#4B5E5E]">AI usage is metered transparently. Failed operations are not charged, and top-ups never change your subscription limits.</p>
        </div>
      </section>

      {/* Comparison table */}
      <section className="bg-[#F7FAF9] py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="max-w-3xl">
            <span className="text-xs md:text-sm tracking-[0.2em] uppercase font-bold text-[#D96C4A]">Plan comparison</span>
            <h2 className="mt-4 font-display text-3xl md:text-5xl font-semibold leading-[1.05] text-[#0A2424]">
              See exactly what you get.
            </h2>
          </div>
          <div className="mt-10 overflow-x-auto rounded-3xl border border-[#0E6B6B]/12 bg-white">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#0E6B6B]/10">
                  <th className="p-5 font-display font-semibold text-[#0A2424]">Feature</th>
                  <th className="p-5 font-display font-semibold text-[#0A2424] text-center">Solo</th>
                  <th className="p-5 font-display font-semibold text-[#0A2424] text-center">Starter</th>
                  <th className="p-5 font-display font-semibold text-[#0E6B6B] text-center">Growth</th>
                  <th className="p-5 font-display font-semibold text-[#0A2424] text-center">Scale</th>
                </tr>
              </thead>
              <tbody>
                {compare.map((row) => (
                  <tr key={row.label} className="border-b border-[#0E6B6B]/8 last:border-0">
                    <td className="p-5 text-[15px] text-[#1c2c2c]">{row.label}</td>
                    <td className="p-5 text-center">
                      {row.o === true ? <Check className="h-5 w-5 text-[#0E6B6B] mx-auto" /> : row.o ? <span className="font-semibold text-[#0A2424]">{row.o}</span> : <X className="h-5 w-5 text-[#0E6B6B]/25 mx-auto" />}
                    </td>
                    <td className="p-5 text-center">
                      {row.s === true ? <Check className="h-5 w-5 text-[#0E6B6B] mx-auto" /> : row.s ? <span className="font-semibold text-[#0A2424]">{row.s}</span> : <X className="h-5 w-5 text-[#0E6B6B]/25 mx-auto" />}
                    </td>
                    <td className="p-5 text-center bg-[#0E6B6B]/4">
                      {row.g === true ? <Check className="h-5 w-5 text-[#0E6B6B] mx-auto" /> : row.g ? <span className="font-semibold text-[#0E6B6B]">{row.g}</span> : <X className="h-5 w-5 text-[#0E6B6B]/25 mx-auto" />}
                    </td>
                    <td className="p-5 text-center">
                      {row.e === true ? <Check className="h-5 w-5 text-[#0E6B6B] mx-auto" /> : row.e ? <span className="font-semibold text-[#0A2424]">{row.e}</span> : <X className="h-5 w-5 text-[#0E6B6B]/25 mx-auto" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Pricing FAQ */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#D96C4A]">Pricing FAQ</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold leading-tight text-[#0A2424]">
              Common questions about pricing.
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-6">
                {pricingFaq.map((f, i) => (
              <div key={f.q} data-testid={`pricing-faq-${i}`} className="border-b border-[#0E6B6B]/12 pb-6">
                <h3 className="font-display text-xl font-semibold text-[#0A2424]">{f.q}</h3>
                <p className="mt-2 text-[15px] text-[#4B5E5E] leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageBottomCTA
        eyebrow="Start free"
        title="Pay for the capacity you need—not a different product."
        body="Start with the same connected operating record, then choose the branch, team, and product limits that fit the business."
      />
    </>
  );
}
